import axios, { AxiosError } from 'axios'
import { toast } from 'sonner'
import type { InternalAxiosRequestConfig } from 'axios'

import { API_BASE_URL } from '@/lib/config'
import { m } from '@/lib/paraglide/messages'
import { trackApiError } from '@/lib/recent-api-errors'
import {
    SERVER_TIME_HEADER_NAME,
    recordServerTime,
} from '@/lib/server-time'

interface RetryableRequest extends InternalAxiosRequestConfig {
    _retry?: boolean
}

export { API_BASE_URL }

export interface ApiErrorInfo {
    code: string
    message: string
    // Details có thể là validation map (Record<string,string>) hoặc structured
    // domain payload (vd: PURCHASE_INSUFFICIENT → array of insufficient details).
    details?: Record<string, string> | unknown
}

export interface ApiResponse<T> {
    success: boolean
    data?: T
    error?: ApiErrorInfo
    meta?: {
        page: number
        per_page: number
        total: number
        total_pages: number
    }
}

export class ApiError extends Error {
    constructor(
        public status: number,
        public code: string,
        message: string,
        public details?: Record<string, string> | unknown,
        // Full-UUID trace ID echoed by the server (X-Request-ID). Surfaced in
        // error toasts so users can quote it when reporting bugs — admins
        // grep server logs by req_id to pull the full call chain.
        public requestId?: string,
    ) {
        super(message)
        this.name = 'ApiError'
    }
}

const instance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
})

// Auto-set Content-Type: FormData → let browser handle, otherwise JSON
instance.interceptors.request.use((config) => {
    if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json'
    }
    return config
})

// Silent token refresh state — prevents concurrent refresh calls
let refreshPromise: Promise<boolean> | null = null

// A hung request (TCP half-open after sleep, captive portal) must settle:
// refreshPromise is deduped, so without a timeout every future refresh would
// await the same dead request forever. Exported because callers that put their
// own deadline around tryRefreshToken() must pick one LONGER than this — a
// shorter outer deadline gives up on a request that is still deduped in
// flight, so their "retries" all await the same hung call.
export const REFRESH_HTTP_TIMEOUT_MS = 10_000

export async function tryRefreshToken(): Promise<boolean> {
    if (refreshPromise) return refreshPromise
    refreshPromise = axios
        .post(`${API_BASE_URL}/auth/refresh`, null, {
            withCredentials: true,
            timeout: REFRESH_HTTP_TIMEOUT_MS,
        })
        .then(() => true)
        .catch(() => false)
        .finally(() => {
            refreshPromise = null
        })
    return refreshPromise
}

// Public routes that must render for unauthenticated visitors (e.g. a customer
// scanning a traceability QR). A background 401 from the auth probe must NOT
// bounce these pages to the login screen.
const PUBLIC_PATH_PREFIXES = ['/login', '/trace/', '/customer-display']

function redirectToLogin() {
    const currentPath = window.location.pathname
    const onPublicPage = PUBLIC_PATH_PREFIXES.some(
        (p) => currentPath === p || currentPath.startsWith(p),
    )
    if (!onPublicPage) {
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
    }
}

function shouldSkipTokenRefresh(url?: string) {
    if (!url) return true

    const path = url.split('?')[0]
    return [
        '/auth/login',
        '/auth/register',
        '/auth/refresh',
        '/auth/logout',
    ].includes(path)
}

// Response interceptor: unwrap data, handle 401 refresh
instance.interceptors.response.use(
    (res) => {
        // Track server clock skew — every response carries X-Server-Time so
        // consumers (SLA badge, countdown) can render "now" via server time.
        recordServerTime(
            res.headers?.[SERVER_TIME_HEADER_NAME] as string | undefined,
        )
        return res
    },
    async (error: AxiosError<ApiResponse<unknown>>) => {
        // Also refresh offset on error responses — the header is still stamped.
        recordServerTime(
            error.response?.headers?.[SERVER_TIME_HEADER_NAME] as
                | string
                | undefined,
        )
        const originalRequest = error.config as RetryableRequest | undefined
        // Read-only impersonation backstop: a blocked write returns 403 with this
        // code. Show a friendly, deduped toast — do NOT redirect or trigger the
        // 401-refresh path. A stable toast id collapses background-POST repeats.
        if (
            error.response?.status === 403 &&
            error.response.data?.error?.code === 'IMPERSONATION_READ_ONLY'
        ) {
            toast.error(m.impersonation_readonly_toast(), {
                id: 'impersonation-readonly',
            })
            return Promise.reject(error)
        }
        if (
            error.response?.status === 401 &&
            originalRequest &&
            !shouldSkipTokenRefresh(originalRequest.url) &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true
            const refreshed = await tryRefreshToken()
            if (refreshed) {
                return instance(originalRequest)
            }
            redirectToLogin()
        }
        return Promise.reject(error)
    },
)

async function request<T>(
    method: string,
    endpoint: string,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined>,
): Promise<ApiResponse<T>> {
    try {
        const res = await instance.request<ApiResponse<T>>({
            method,
            url: endpoint,
            data: body,
            params,
        })
        if (res.status === 204) {
            return { success: true } as ApiResponse<T>
        }
        return res.data
    } catch (err) {
        // This catch runs AFTER the 401 silent-refresh interceptor, so only
        // errors that truly surface to the caller are tracked — refresh-retry
        // churn never reaches telemetry.
        if (err instanceof AxiosError && err.response?.data?.error) {
            const info = err.response.data.error as ApiErrorInfo
            const reqId = err.response.headers?.['x-request-id'] as
                | string
                | undefined
            const apiError = new ApiError(
                err.response.status,
                info.code || 'UNKNOWN_ERROR',
                info.message || 'An error occurred',
                info.details,
                reqId,
            )
            trackApiError({
                requestId: reqId,
                status: apiError.status,
                code: apiError.code,
                method,
                path: endpoint,
                message: apiError.message,
            })
            throw apiError
        }
        if (err instanceof AxiosError) {
            const reqId = err.response?.headers?.['x-request-id'] as
                | string
                | undefined
            const apiError = new ApiError(
                err.response?.status ?? 0,
                'NETWORK_ERROR',
                err.message,
                undefined,
                reqId,
            )
            trackApiError({
                requestId: reqId,
                status: apiError.status,
                code: apiError.code,
                method,
                path: endpoint,
                message: apiError.message,
            })
            throw apiError
        }
        throw err
    }
}

export const api = {
    // params is typed `object` (not a generic Record) so callers can pass a
    // typed params interface while still providing an explicit response type
    // argument — with two type params, passing only <T> would otherwise force
    // TParams to its default and reject the interface (no index signature).
    // getBlob tải file nhị phân qua CÙNG instance với mọi call khác, nhờ đó
    // dùng chung baseURL và interceptor refresh 401 (axios trần sẽ bỏ qua cả
    // hai: hết phiên là tải file hỏng vĩnh viễn cho tới khi query khác gia hạn
    // hộ). Trả nguyên response để caller đọc được Content-Disposition.
    getBlob: (endpoint: string, options?: { params?: object }) =>
        instance.get<Blob>(endpoint, {
            params: options?.params,
            responseType: 'blob',
        }),

    get: <T>(endpoint: string, options?: { params?: object }) =>
        request<T>(
            'GET',
            endpoint,
            undefined,
            options?.params as
                | Record<string, string | number | boolean | undefined>
                | undefined,
        ),

    post: <T>(endpoint: string, body?: unknown) =>
        request<T>('POST', endpoint, body),

    put: <T>(endpoint: string, body?: unknown) =>
        request<T>('PUT', endpoint, body),

    patch: <T>(endpoint: string, body?: unknown) =>
        request<T>('PATCH', endpoint, body),

    delete: <T>(endpoint: string) => request<T>('DELETE', endpoint),
}
